import { PencilIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { Banner } from "@moe/oss/ui/banner";
import { Button } from "@moe/oss/ui/button";
import { CharacterService } from "@moe/priv/model/character";
import { Profile } from "@moe/priv/model/profile";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FollowButton } from "@web/components/FollowButton";
import { LoadingSpinner } from "@web/components/LoadingSpinner";
import { Navbar } from "@web/components/Navbar";
import { UnAuthedPage } from "@web/components/UnAuthedPage";
import { useInfiniteScroll } from "@web/hooks/use-infinite-scroll";
import { Badges } from "@web/route-services/profile/Badges";
import { BelowAvatar, BelowAvatarSkeleton } from "@web/route-services/profile/BelowAvatar";
import { ProfileCard } from "@web/route-services/profile/ProfileCard";
import { useAppContext } from "@web/route-services/root/AppContext";
import { motion } from "motion/react";
import { DeepPartial } from "react-hook-form";
import { z } from "zod";
const pageSize = 25;

/*
TODO: this is a mess we need to clean this up

- if username or external username param are available search for that profile
  - if external username 
    - don't query for the profile, just use the username + banner
    - the getPage query should be given an externalCreator filter field

  - if internal username
    - query for the profile
    - give the getPage query a creator field

  - if both are available, use the internal username

- if neither internal username or external username is available
  - query by session

- if session, internal username, and external username are all missing
  - show <UnAuthed />
*/
const searchSchema = z.union([
  z.object({ username: z.string().optional() }),
  z.object({ externalUsername: z.string().optional() })
]);

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
  validateSearch: searchSchema
});

function ProfilePage() {
  const { access, session } = useAppContext();
  const searchParams = Route.useSearch();

  let username: string | undefined;
  const isInternal = "username" in searchParams;

  if ("username" in searchParams) {
    username = searchParams.username;
  } else if ("externalUsername" in searchParams) {
    username = searchParams.externalUsername;
  }

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ["profile", username, session?.user.id, isInternal],
    queryFn: async () => {
      if (username && isInternal) return await access.profile.getByUsername(username);
      if (session?.user.id && !username) return await access.profile.getByID(session?.user.id);
    },
    enabled: !!session || (!!username && isInternal)
  });

  const {
    data: charsPages,
    isPending: isCharsPending,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: ["profileCards", username, isInternal, profile],
    queryFn: async ({ pageParam = 0 }) => {
      const filter: any = {};
      if (username && isInternal) filter.creator = username;
      if (username && !isInternal) filter.externalCreator = username;
      if (!username && profile) filter.creator = profile.username;

      const pageOptions: DeepPartial<CharacterService.GetPageOptions> = {
        page: pageParam,
        pageSize,
        order: "desc",
        ranking: "recency",
        filter: {
          ...filter,
          nsfwOK: true,
          privateOK: true
        }
      };
      return await access.character.getPage(pageOptions);
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.length < pageSize) return undefined;
      return (lastPageParam as number) + 1;
    },
    initialPageParam: 0,
    enabled: !!username || !!profile
  });

  const chars = charsPages?.pages.flatMap((page) => page) ?? [];

  const scrolledToBottomHandler = () => {
    fetchNextPage();
  };

  const { bottomSentinelRef } = useInfiniteScroll({ items: chars, scrolledToBottomHandler });

  if (!session && !username) return <UnAuthedPage />;

  return (
    <div className="from-background-gradient via-background to-background text-tx-primary flex h-screen w-screen bg-gradient-to-b text-sm antialiased lg:text-base">
      <Navbar />
      {/* profile container */}
      {(username || session?.user.id) && (
        <motion.div
          className="border-line relative m-auto flex h-[98vh] w-full flex-col  items-center overflow-hidden rounded-3xl border md:h-[50rem] md:w-[40rem]"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: [0, 0.71, 0.2, 1.01]
          }}
        >
          <div className="relative h-fit w-full shrink-0">
            {/* top = height of banner - 1/2 height of avatar */}
            <div className="absolute left-5 top-[5rem] z-10 flex-col justify-center md:left-8 md:top-[8.5rem] ">
              <Avatar
                src={profile?.avatar}
                className="border-container-primary mb-2 h-24 w-24 border-4 md:h-32 md:w-32 md:border-8"
              />
              {isProfileLoading && <BelowAvatarSkeleton />}
              {profile && <BelowAvatar profile={profile} />}
            </div>
            {/* banner */}
            <div className="bg-container-secondary relative flex h-32 w-full items-center justify-center  rounded-t-xl opacity-75 md:h-48">
              <Banner src={profile?.banner} alt="Banner" className="h-full w-full" />
              <div className="absolute right-2 top-3">
                <Action profile={profile} isLoading={isProfileLoading} />
              </div>
            </div>

            {/* header */}
            <div className="bg-container-primary flex h-36 w-full md:h-44">
              {/* Spacer */}
              <div className="h-full w-44 shrink-0  md:w-56"></div>
              {/* right of header */}
              <div className="mr-4 mt-4 flex grow flex-col pb-4 md:mt-6">
                <h1 className="text-tx-primary mb-0.5 line-clamp-1 shrink-0 text-base font-semibold md:mb-2 md:text-xl">
                  {profile?.username || username}
                </h1>
                <div className="scroll-hidden grow overflow-auto">
                  <p className="text-tx-tertiary mt-1.5 w-full  text-ellipsis text-xs font-semibold md:font-medium">
                    {profile?.bio}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Profile body */}
          <div className="scroll-secondary flex w-full grow flex-col space-y-2 overflow-y-auto px-3 pb-12">
            {/* Badges */}
            {!!profile?.badges.length && (
              <>
                <h1 className="text-tx-primary pb-3 pl-4 pt-6 text-2xl font-semibold">Badges</h1>
                <Badges badges={profile?.badges ?? []} />
              </>
            )}
            <h1 className="text-tx-primary pb-3 pl-4 pt-6 text-2xl font-semibold">Characters</h1>
            {isCharsPending && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full w-full items-center justify-center pb-10"
              >
                <LoadingSpinner />
              </motion.div>
            )}
            {!isCharsPending && chars.length === 0 && (
              <motion.div
                key="no-cards"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full w-full items-center justify-center pb-10"
              >
                <p className="text-tx-tertiary select-none text-center text-lg font-[650]">
                  (づ ◕‿◕ )づ No cards yet...
                  <br />
                  Create a card by going to{" "}
                  <Link to="/studio/create" className="text-tx-secondary hover:underline">
                    Studio
                  </Link>
                  .
                </p>
              </motion.div>
            )}
            {!isCharsPending && chars.length > 0 && (
              <motion.div key="card-list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {chars.map((card) => (
                  <ProfileCard
                    key={card.id}
                    char={card}
                    showEdit={profile?.id === session?.user.id}
                    showDelete={profile?.id === session?.user.id}
                  />
                ))}
              </motion.div>
            )}
            <div ref={bottomSentinelRef} className="h-2 w-full" />
          </div>
        </motion.div>
      )}
    </div>
  );
}

interface ActionProps {
  profile?: Profile;
  isLoading: boolean;
}
function Action({ profile, isLoading }: ActionProps) {
  const { session } = useAppContext();
  const navigate = useNavigate();
  const profileEditHandler = () => {
    navigate({
      to: "/settings/profile"
    });
  };
  return (
    <div>
      {/* Loading skeleton */}
      {isLoading && (
        <motion.div
          initial={{ opacity: 0.7 }}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <div className="bg-muted h-8 w-12 rounded-md md:w-20" />
        </motion.div>
      )}

      {/* Edit button */}
      {profile && profile.id === session?.user.id && (
        <Button
          className="flex items-center justify-center space-x-2 rounded-xl shadow-lg"
          onClick={profileEditHandler}
        >
          <PencilIcon />
          Edit
        </Button>
      )}

      {/* Follow button */}
      {profile && profile.id !== session?.user.id && session && <FollowButton profile={profile} />}
    </div>
  );
}
